import { useEffect, useState } from "react";
import { Box, Button, Dialog, FormControl, InputLabel, makeStyles, MenuItem, Select } from "@material-ui/core"
import DateInputField, { getCurrentDate } from "../ui/DateInputField";
import NumberInputField from "../ui/NumberInputField";
import Http from "../../engine/Http";
import download from "downloadjs";
import CustomerOfferItemsAccordion from "./CustomerOfferItemsAccordion";
import PriceListSelection from "../pricing/PriceListSelection";
import useFormApi, { } from "../../api/FormApi";
import useShapeApi from "../../api/ShapeApi";
import useKitApi from "../../api/KitApi";
import useSpringMaterialCustomerPriceApi, { convertSpringPricesToMaterialIdIndexedArray, SpringMaterialCustomerPrice } from "../../api/SpringMaterialCustomerPriceApi";
import usePricedSpringApi, { PricedSpring } from "../../api/PricedSpringApi";
import { formatCurrency } from "../../engine/PriceUtilities";
import useHttp from "../../security/Http.hook";

interface Props {
  readonly customerId: number;
  readonly open: boolean;
  readonly onCancel: () => void;
}

export interface OfferItem {
  id: number;
  name: string;
  price: string;
  material: string;
}

interface SpringOfferItem {
  id: number;
  name: string;
  layout: string;
  material: string;
  price: string;
}

const enum Transport {
  Free = 'free',
  Self = 'self'
}

const enum Payment {
  Prepaid = 'prepaid',
  Cash = 'cash',
  Transfer = 'transfer'
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  },
  dialogPaper: {
    minWidth: 640
  },
  button: {
    marginTop: theme.spacing(1)
  },
  inputField: {
    width: 480
  }
}));

const convertToOfferItem = (item: any) : OfferItem => {
  let price;
  if (item.price) {
    price = item.price?.effective;
  } else if (item.unitPrice) {
    price = item.unitPrice?.effective;
  } else {
    price = item.elementsPrice + item.packagingPrice;
  }
  return {
    id: item.id,
    name: item.name,
    price,
    material: item.material?.name
  }
};

const convertPricedSpringToSpringOfferItem = (pricedSpring: PricedSpring) : SpringOfferItem => {
  const wireThicknesses = pricedSpring.materials.map(material => material.thickness);
  const wire = Array.from(new Set(wireThicknesses)).join(' / ').replaceAll('.', ',');
  const price = formatCurrency(pricedSpring.price);
  return {
    id: pricedSpring.id || 0,
    name: pricedSpring.name,
    layout: `${pricedSpring.width}x${pricedSpring.length}`,
    material: wire,
    price: price === undefined ? '?' : price
  }
};

const convertPricedSpringsToSpringOfferItems = (pricedSprings: PricedSpring[]) : SpringOfferItem[] => {
  return pricedSprings.map(pocketSpring => convertPricedSpringToSpringOfferItem(pocketSpring));
};

const convertToOfferItems = (items: any[]) : OfferItem[] => items.map(convertToOfferItem);

const CustomerOfferDialog = (props: Props) => {
  const classes = useStyles();
  const http = useHttp();
  const formApi = useFormApi();
  const shapeApi = useShapeApi();
  const kitApi = useKitApi();
  const springMaterialCustomerPriceApi = useSpringMaterialCustomerPriceApi();
  const pricedSpringApi = usePricedSpringApi();
  const customerId = props.customerId;
  const [springMatrialPrices, setSpringMatrialPrices] = useState<SpringMaterialCustomerPrice[]>();
  const [priceListId, setPriceListId] = useState<number|null>();
  const [validFromDate, setValidFromDate] = useState<string | null>();
  const [transport, setTransport] = useState<Transport | undefined>();
  const [payment, setPayment] = useState<Payment | undefined>();
  const [bankTransferDue, setBankTransferDue] = useState<number | null>();
  const [merchantLimit, setMerchantLimit] = useState<number | null>();
  const [fetchedForms, setFetchedForms] = useState<OfferItem[]>([]);
  const [fetchedShapes, setFetchedShapes] = useState<OfferItem[]>([]);
  const [fetchedKits, setFetchedKits] = useState<OfferItem[]>([]);
  const [fetchedBonnellSprings, setFetchedBonnellSprings] = useState<OfferItem[]>([]);
  const [fetchedPocketSprings, setFetchedPocketSprings] = useState<OfferItem[]>([]);
  const [selectedFormOfferItems, setSelectedFormOfferItems] = useState<OfferItem[]>([]);
  const [selectedShapeOfferItems, setSelectedShapeOfferItems] = useState<OfferItem[]>([]);
  const [selectedKitOfferItems, setSelectedKitOfferItems] = useState<OfferItem[]>([]);
  const [selectedBonnellOfferItems, setSelectedBonnellOfferItems] = useState<OfferItem[]>([]);
  const [selectedPocketOfferItems, setSelectedPocketOfferItems] = useState<OfferItem[]>([]);

  const handleTransportChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const transport = event.target.value as Transport;
    setTransport(transport);
  };

  const handlePaymentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const payment = event.target.value as Payment;
    setPayment(payment);
  };

  const handleCancelButtonClicked = () => {
    setTransport(undefined);
    setValidFromDate(undefined);
    setPayment(undefined);
    setBankTransferDue(undefined);
    setMerchantLimit(undefined);
    setFetchedForms([]);
    setSelectedFormOfferItems([]);
    setFetchedShapes([]);
    setSelectedShapeOfferItems([]);
    setFetchedKits([]);
    setSelectedKitOfferItems([]);
    setFetchedBonnellSprings([]);
    setSelectedBonnellOfferItems([]);
    setFetchedPocketSprings([]);
    setSelectedPocketOfferItems([]);
    props.onCancel();
  };

  const handlePrintButtonClicked = async () => {
    const customer = await http.get(`/api/customers/${props.customerId}`);
    const offer = {
      customer: customer.name,
      forms: selectedFormOfferItems,
      shapes: selectedShapeOfferItems,
      kits: selectedKitOfferItems,
      pockets: selectedPocketOfferItems,
      bonnells: selectedBonnellOfferItems,
      currentDate: getCurrentDate(),
      transport,
      validFromDate,
      payment,
      bankTransferDue,
      merchantLimit
    };
    Http
      .postForFile(`/api/printouts/offers`, offer)
      .then(response => response.blob())
      .then(blob => download(blob, 'oferta.pdf'));
  };

  const handlePriceListSelected = (priceListId: number) => {
    setPriceListId(priceListId);
  };

  useEffect(() => {
    if (props.open === true) {
      setSelectedFormOfferItems([]);
      setSelectedShapeOfferItems([]);
      setSelectedKitOfferItems([]);
      setSelectedBonnellOfferItems([]);
      setSelectedPocketOfferItems([]);
      if (priceListId !== undefined && priceListId !== null) {
        formApi
          .fetchForms(customerId, priceListId)
          .then(convertToOfferItems)
          .then(setFetchedForms);
        shapeApi
          .fetchShapes(customerId, priceListId)
          .then(convertToOfferItems)
          .then(setFetchedShapes);
        kitApi
          .fetchKits(customerId, priceListId)
          .then(convertToOfferItems)
          .then(setFetchedKits);
        springMaterialCustomerPriceApi
          .fetchSpringMaterialCustomerPrices(customerId, priceListId)
          .then(convertSpringPricesToMaterialIdIndexedArray)
          .then(setSpringMatrialPrices);
      }
    }
  }, [props.open, customerId, priceListId]);

  useEffect(() => {
    if (priceListId !== undefined && priceListId !== null) {
      pricedSpringApi
        .fetchPricedBonnellSprings(customerId, priceListId)
        .then(convertPricedSpringsToSpringOfferItems)
        .then(setFetchedBonnellSprings);
      pricedSpringApi
        .fetchPricedPocketSprings(customerId, priceListId)
        .then(convertPricedSpringsToSpringOfferItems)
        .then(setFetchedPocketSprings);
    }
  }, [springMatrialPrices]);

  return (
    <Dialog open={props.open} classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.dialogContent}>
        <h2>Parametry wydruku oferty</h2>
        <PriceListSelection
          defaultPriceListId={ priceListId === null ? undefined : priceListId }
          onPriceListSelected={ handlePriceListSelected }
        />
        <CustomerOfferItemsAccordion
          title='Formatki'
          promptMessage='Wybierz formatkę do wydruku oferty i ustaw jej pozycję'
          offerItems={ fetchedForms }
          onOfferItemsSelected={ setSelectedFormOfferItems }
        />
        <CustomerOfferItemsAccordion
          title='Wykroje'
          promptMessage='Wybierz wykrój do wydruku oferty i ustaw jego pozycję'
          offerItems={fetchedShapes}
          onOfferItemsSelected={setSelectedShapeOfferItems}
        />
        <CustomerOfferItemsAccordion
          title='Komplety'
          promptMessage='Wybierz komplet do wydruku oferty i ustaw jego pozycję'
          offerItems={fetchedKits}
          onOfferItemsSelected={setSelectedKitOfferItems}
        />
        <CustomerOfferItemsAccordion
          title='Bonnelle'
          promptMessage='Wybierz bonnell do wydruku oferty i ustaw jego pozycję'
          offerItems={fetchedBonnellSprings}
          onOfferItemsSelected={setSelectedBonnellOfferItems}
        />
        <CustomerOfferItemsAccordion
          title='Pockety'
          promptMessage='Wybierz pocket do wydruku oferty i ustaw jego pozycję'
          offerItems={fetchedPocketSprings}
          onOfferItemsSelected={setSelectedPocketOfferItems}
        />
        <Box display='flex' flexDirection='row'>
          <FormControl>
            <InputLabel id="transport-label">Transport</InputLabel>
            <Select className={classes.inputField}
              labelId="transport-label"
              value={transport || null}
              onChange={handleTransportChange}>
                <MenuItem value={undefined}>brak informacji</MenuItem>
                <MenuItem value={Transport.Self}>odbiór własny</MenuItem>
                <MenuItem value={Transport.Free}>po naszej stronie</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display='flex' flexDirection='row'>
          <DateInputField
            classes={classes}
            label='Początek obowiązywania oferty'
            initialValue={getCurrentDate()}
            calculatedValue={validFromDate}
            onValueChange={setValidFromDate}
          />
        </Box>
        <Box display='flex' flexDirection='row'>
          <FormControl>
            <InputLabel id="payment-label">Warunki płatności</InputLabel>
            <Select
              className={classes.inputField}
              labelId="transport-label"
              value={payment || null}
              onChange={handlePaymentChange}>
                <MenuItem value={undefined}>brak informacji</MenuItem>
                <MenuItem value={Payment.Prepaid}>przedpłata (proforma)</MenuItem>
                <MenuItem value={Payment.Cash}>płatność gotówką</MenuItem>
                <MenuItem value={Payment.Transfer}>przelew bankowy</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display='flex' flexDirection='row'>
          <NumberInputField
            classes={classes}
            label="Termin przelewu bankowego"
            calculatedValue={bankTransferDue}
            onValueChange={setBankTransferDue}
            disabled={payment !== Payment.Transfer}
          />
        </Box>
        <Box display='flex' flexDirection='row'>
          <NumberInputField
            classes={classes}
            label="Początkowy limit kupiecki"
            calculatedValue={merchantLimit}
            onValueChange={setMerchantLimit}
          />
        </Box>
        <Button
          color='primary'
          className={classes.button}
          onClick={handlePrintButtonClicked}
          variant='contained'>
            Drukuj
        </Button>
        <Button
          color='secondary'
          className={classes.button}
          onClick={handleCancelButtonClicked}
          variant='contained'>
            Anuluj
        </Button>
      </Box>
    </Dialog>
  )
};

export default CustomerOfferDialog;